import React from 'react';
import castArray from 'lodash/castArray';
import PropTypes from 'prop-types';
import { hasRole, can } from '../../utils/auth';

/**
 * Ensures user has the correct permissions before rendering. In order to be
 * authorized you either need to have ONE of the roles specified, or ALL of
 * the permissions specified.
 *
 * Either the `roles` or `permissions` props are necessary, otherwise the
 * children nodes will not be displayed.
 *
 * @example
 *
 *    <Protected permissions="viewInsights"><InsightsPage /></Protected>
 *    <Protected fallback={NotAuthorizedPage} permissions="editContract"><Page /></Protected>
 *    <Protected permissions={['editAgreement', 'createProduct']}><Page /></Protected>
 *    <Protected roles="msft"><ShowMsft /></Protected>
 *    <Protected permissions={['manageDRIOps', 'generateStatement']} or={true}><Generate Statement Page /></Protected>
 */
const Protected = ({ roles, permissions, fallback, children, orPermissions }) => {
  if (orPermissions) {
    const hasPermission = castArray(permissions).some(permission => {
      return can(castArray(permission));
    });

    return hasRole(...castArray(roles)) || hasPermission ? (
      <>{children}</>
    ) : (
      <>{typeof fallback === 'function' ? fallback() : fallback}</>
    );
  }

  // if the user has any of the allowed roles, or they have all specified
  // permissions, we should should show the page.
  return hasRole(...castArray(roles)) || can(...castArray(permissions)) ? (
    <>{children}</>
  ) : (
    <>{typeof fallback === 'function' ? fallback() : fallback}</>
  );
};

Protected.propTypes = {
  /**
   * Allowed Roles
   */
  roles: PropTypes.oneOfType([
    PropTypes.string.isRequired,
    PropTypes.arrayOf(PropTypes.string.isRequired),
  ]),
  /**
   * Required Permissions
   */
  permissions: PropTypes.oneOfType([
    PropTypes.string.isRequired,
    PropTypes.arrayOf(PropTypes.string.isRequired),
  ]),
  /**
   * What to render if the user isn't authorized
   */
  fallback: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  /**
   * What to render if the user is authorized
   */
  children: PropTypes.node.isRequired,
  /**
   * Whether the user has to have ALL permissions to be granted access (default)
   * or whether the user only has to have one of the permissions to be granted access
   */
  orPermissions: PropTypes.bool,
};

Protected.defaultProps = {
  roles: [],
  permissions: [],
  fallback: null,
  orPermissions: false,
};

export default Protected;
